import React, { useState, useEffect, useCallback, useMemo } from "react";
import heic2any from "heic2any";

import {
  Grid,
  Box,
  Button,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Radio,
  RadioGroup,
  Autocomplete,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { Formik, Field, Form, useFormikContext } from "formik";
import { FormHelperText } from "@mui/material";
import * as Constants from "constants/index";
import { personalInformationValidationSchema } from "validations/validationPersonalInformation";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Badge from "@mui/material/Badge";
import axios from "axios";
import { notification } from "antd";
import InstructionText from "../shared/InstructionText";

// [SECURITY] Import security utilities for data sanitization and validation
import {
  validateQcIdFormat,
  sanitizeHtmlString,
  getErrorMessage,
  logErrorSecurely,
  processQcidApiResponse,
} from "utils/securityUtils";

// Allowed image file extensions for upload
const ALLOWED_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heif', '.heic', '.hei'];

/**
 * Validates if the uploaded file has an allowed extension
 * @param {File} file - The file object to validate
 * @returns {boolean} - True if file extension is allowed, false otherwise
 */
const validateFileExtension = (file) => {
  if (!file) return false;
  
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
  
  return ALLOWED_FILE_EXTENSIONS.includes(fileExtension);
};

/**
 * Personal Information Form Component
 * 
 * Handles child and parent/guardian information collection with QC ID auto-fill
 * security features including input validation, XSS sanitization, and secure error handling.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.handleNext - Callback to proceed to next step
 * @param {Function} props.onInformationChange - Callback when form data changes
 * @param {Object} props.personalInformation - Initial form values
 * @returns {JSX.Element} Personal information form component
 */
function PersonalInformation({
  handleNext,
  onInformationChange,
  personalInformation,
}) {
  const QCID_TOOLTIP_TEXT = "QC ID ang gamit para punan ang info mo.";
  const [bloodTypeOptions, setBloodTypeOptions] = useState(
    Constants.bloodTypeOptions
  );
  const [civilStatusOptions, setCivilStatusOptions] = useState(
    Constants.civilStatusOptions
  );

  // Guardian's information
  const [educationalAttainmentOptions, setEducationalAttainmentOptions] =
    useState(Constants.EDUCATIONAL_ATTAINMENT_OPTIONS);

  // Save data
  const [currentPersonalInformation, setCurrentPersonalInformation] =
    useState(personalInformation);
  const [panel1Expanded, setPanel1Expanded] = useState(true);
  const [panel2Expanded, setPanel2Expanded] = useState(false);
  const [barangayList, setBarangayList] = useState([]);
  
  // QC ID auto-population state
  const [qcidPopulatedFields, setQcidPopulatedFields] = useState(new Set());
  const [isQcidLoading, setIsQcidLoading] = useState(false);
  const [qcidData, setQcidData] = useState(null);
  
  // Parent QC ID auto-population state
  const [parentQcidPopulatedFields, setParentQcidPopulatedFields] = useState(new Set());
  const [isParentQcidLoading, setIsParentQcidLoading] = useState(false);
  const [parentQcidData, setParentQcidData] = useState(null);
  
  // Image preview state for HEIC conversion
  const [certificateImagePreview, setCertificateImagePreview] = useState(null);
  const [isConvertingImage, setIsConvertingImage] = useState(false);

  const barangayData = Constants.barangayData;

  useEffect(() => {
    getBarangay();
  }, []);

  // Update internal state when personalInformation prop changes (for draft restoration)
  useEffect(() => {
    setCurrentPersonalInformation(personalInformation);
  }, [personalInformation]);

  const savePersonalInformation = (values) => {
    onInformationChange(values);
  };

  const parentSectoralMembershipOptions = [
    { label: "PWD", value: "PWD" },
    { label: "Senior Citizen", value: "seniorCitizen" },
    { label: "Solo Parent", value: "soloParent" },
    { label: " Indigent", value: "Indigent" },
    { label: "N/A", value: "IbaPa" }, // Changed from "Iba pa" to "N/A" for data consistency
  ];

  const parentSocialProtectionOptions = [
    { label: "SSS", value: "SSS" },
    { label: "4P's", value: "4Ps" },
    { label: "Pag-IBIG", value: "Pag-IBIG" },
    { label: "GSIS", value: "GSIS" },
    { label: "PhilHealth", value: "PhilHealth" },
    { label: "Insurance (hal. PruLife, Sunlife)", value: "Insurance" },
    { label: "Iba pa", value: "IbaPa" },
  ];

  const elementaryOptions = Constants.elementarySchool;
  const secondaryOptions = Constants.secondarySchool;
  const gradeLevelOptions = Constants.gradeLevel;
  const stoppingStudyReasonsOptions = Constants.stoppingStudyReasons;
  const notStudyingReasonOptions = Constants.notStudyingReason;

  const educationRelatedFields = Constants.educationRelatedFields;

  const householdChoresChildCanDoOptions = [
    {
      value: "Nagliligpit o nag-oorganisa ng mga gamit",
      label: "Nagliligpit o nag-oorganisa ng mga gamit",
    },
    { value: "Naghuhugas ng pinggan", label: "Naghuhugas ng pinggan" },
    { value: "Naghahain ng mesa", label: "Naghahain ng mesa" },
    { value: "Nagwawalis ng paligid", label: "Nagwawalis ng paligid" },
    { value: "Naglalaba", label: "Naglalaba" },
    { value: "Nagsasampay", label: "Nagsasampay" },
    { value: "Nagtutupi ng mga damit", label: "Nagtutupi ng mga damit" },
  ];

  // Sort schools alphabetically and render them as menu items
  const renderSchoolOptions = (schools) =>
    schools.map((item) => (
      <MenuItem key={item} value={item}>
        {item}
      </MenuItem>
    ));

  // Sort schools alphabetically
  const renderSortedSchoolOptions = (schools) => {
    return [...schools].sort((a, b) => a.localeCompare(b));
  };

  const CheckboxOptions = ({ options, name, label }) => {
    const { values, setFieldValue, touched, errors } = useFormikContext();
    const selectedValues = Array.isArray(values[name]) ? values[name] : [];

    const handleChange = useCallback((option) => {
      if (selectedValues.includes(option)) {
        const nextValue = selectedValues.filter((value) => value !== option);
        setFieldValue(name, nextValue);
      } else {
        const nextValue = [...selectedValues, option];
        setFieldValue(name, nextValue);
      }
    }, [selectedValues, name, setFieldValue]);

    const isChecked = useCallback((option) => {
      return selectedValues.includes(option);
    }, [selectedValues]);

    return (
      <Box>
        <Typography>{label}</Typography>
        {options.map((option) => (
          <div key={option}>
            <FormControlLabel
              control={
                <Field
                  type="checkbox"
                  name={name}
                  value={option}
                  as={Checkbox}
                  checked={isChecked(option)}
                  onChange={() => handleChange(option)}
                />
              }
              label={option}
            />
          </div>
        ))}
        <FormHelperText>{touched[name] && errors[name]}</FormHelperText>
      </Box>
    );
  };

  function countErrors(info, errors, touched) {
    let childErrors = 0;
    let parentErrors = 0;

    for (const key in info) {
      if (!info.hasOwnProperty(key)) continue;

      const isParent = key.startsWith("parent");
      const isEmptyArray = Array.isArray(info[key]) && info[key].length === 0;
      const isEmptyString = typeof info[key] === "string" && info[key] === "";

      if ((isEmptyArray || isEmptyString) && errors[key] && touched[key]) {
        isParent ? parentErrors++ : childErrors++;
      }
    }

    return { childErrors, parentErrors };
  }

  const getBarangay = async () => {
    try {
      // [SECBACK0701] Call backend proxy instead of external API directly
      const proxyUrl = `${process.env.REACT_APP_API}/get_barangay`;
      const response = await axios.post(
        proxyUrl,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000
        }
      );

      if (response.status === 200) {
        setBarangayList(response.data);
      } else {
        throw new Error("Failed to fetch barangay list");
      }
    } catch (error) {
      setBarangayList([]);
      notification["error"]({
        message: "Error",
        description: `Unable to fetch barangay list. Please try again later.`,
      });
      console.error("Error fetching barangay list:", error);
    }
  };

  const fetchAndUpdateQcidData = async (qcID, setFieldValue) => {
    // [SECURITY: A03] Input validation - prevent injection attacks
    if (!validateQcIdFormat(qcID)) {
      notification.error({
        message: "Invalid QC ID",
        description: "Please enter a valid 14-16 digit QC ID number.",
      });
      return;
    }

    try {
      setIsQcidLoading(true);
      
      // [SECBACK0701] Call backend proxy instead of external API directly
      const proxyUrl = `${process.env.REACT_APP_API}/qcid_lookup`;
      
      const response = await axios.post(proxyUrl, { qcID }, {
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        timeout: 30000
      });

      if (response.status !== 200) {
        throw new Error(`Server error: ${response.status}`);
      }

      const qcid_data_json = response.data;
      
      // [SECURITY: A05] Validate and sanitize response before using
      const processedResponse = processQcidApiResponse(qcid_data_json);
      if (!processedResponse.success) {
        throw new Error(processedResponse.error);
      }

      const result = processedResponse.data;
      setQcidData(result);
      
      // Track which fields were populated by QC ID
      const populatedFields = new Set();
      
      // [SECURITY: A03] Sanitized data is already safe from processQcidApiResponse
      // Auto-populate child information
      if (result.first_name && result.first_name.trim() !== "") {
        setFieldValue("firstName", result.first_name);
        populatedFields.add("firstName");
      }
      
      if (result.middle_name && result.middle_name.trim() !== "") {
        setFieldValue("middleName", result.middle_name);
        populatedFields.add("middleName");
      }
      
      if (result.last_name && result.last_name.trim() !== "") {
        setFieldValue("surname", result.last_name);
        populatedFields.add("surname");
      }
      
      if (result.suffix_name && result.suffix_name.trim() !== "" && result.suffix_name !== "XX") {
        setFieldValue("extension", result.suffix_name);
        populatedFields.add("extension");
      }
      
      if (result.birthday && result.birthday.trim() !== "") {
        // Convert birthday format from YYYY-M-D to proper date
        const [year, month, day] = result.birthday.split('-');
        const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        setFieldValue("childBirthday", formattedDate);
        populatedFields.add("childBirthday");
      }
      
      if (result.gender_name && result.gender_name.trim() !== "") {
        setFieldValue("gender", result.gender_name);
        populatedFields.add("gender");
      }
      
      // Auto-populate address information
      if (result.house_no && result.house_no.trim() !== "") {
        setFieldValue("houseNo", result.house_no);
        populatedFields.add("houseNo");
      }
      
      if (result.street && result.street.trim() !== "") {
        setFieldValue("street", result.street);
        populatedFields.add("street");
      }
      
      if (result.Barangay && result.Barangay.trim() !== "") {
        setFieldValue("barangay", result.Barangay);
        populatedFields.add("barangay");
        
        // Auto-populate district and zipcode based on barangay
        if (barangayData[result.Barangay]) {
          const { zipCode, district } = barangayData[result.Barangay];
          setFieldValue("zipCode", zipCode);
          setFieldValue("district", district);
        }
      }
      
      if (result.City && result.City.trim() !== "") {
        setFieldValue("city", result.City);
        populatedFields.add("city");
      }
      
      // Mark QC ID as populated to disable it
      populatedFields.add("qcId");
      
      // Store the populated fields
      setQcidPopulatedFields(populatedFields);
      
      notification.success({
        message: "Success",
        description: "QC ID information has been loaded and form fields have been auto-populated.",
        duration: 4,
      });
      
    } catch (error) {
      setQcidData(null);
      setQcidPopulatedFields(new Set());

      // [SECURITY: A09] Use secure error logging - NO PII exposure
      logErrorSecurely(error, 'fetchAndUpdateQcidData');
      
      // [SECURITY: A04] Show generic error message - NO technical details
      const errorMessage = getErrorMessage(error);
      
      notification.error({
        message: "Error",
        description: errorMessage,
        duration: 4,
      });
    } finally {
      setIsQcidLoading(false);
    }
  };

  // Function to clear QC ID data and re-enable fields
  const clearQcidData = (setFieldValue) => {
    // Clear the populated fields
    qcidPopulatedFields.forEach(fieldName => {
      setFieldValue(fieldName, "");
    });
    
    // Clear QC ID field
    setFieldValue("qcId", "");
    
    // Reset state
    setQcidData(null);
    setQcidPopulatedFields(new Set());
    
    notification.info({
      message: "Cleared",
      description: "QC ID data has been cleared. You can now fill the form manually or enter a new QC ID.",
      duration: 3,
    });
  };

  // Function for parent QC ID auto-population
  const fetchAndUpdateParentQcidData = async (qcID, setFieldValue) => {
    // [SECURITY: A03] Input validation - prevent injection attacks
    if (!validateQcIdFormat(qcID)) {
      notification.error({
        message: "Invalid QC ID",
        description: "Please enter a valid 14-16 digit QC ID number.",
      });
      return;
    }

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    };

    try {
      // [SECBACK0701] Timeout enforcement - prevent resource exhaustion
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      setIsParentQcidLoading(true);
      
      // [SECBACK0701] Call backend proxy instead of external API directly
      const proxyUrl = `${process.env.REACT_APP_API}/qcid_lookup`;
      
      const qcid_data = await fetch(proxyUrl, {
        ...requestOptions,
        body: JSON.stringify({ qcID }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!qcid_data.ok) {
        throw new Error(`Server error: ${qcid_data.status}`);
      }

      const qcid_data_json = await qcid_data.json();
      
      // [SECURITY: A05] Validate and sanitize response before using
      const processedResponse = processQcidApiResponse(qcid_data_json);
      if (!processedResponse.success) {
        throw new Error(processedResponse.error);
      }

      const result = processedResponse.data;
      setParentQcidData(result);
      
      // Track which fields were populated by QC ID
      const populatedFields = new Set();
      
      // [SECURITY: A03] Sanitized data is already safe from processQcidApiResponse
      // Auto-populate parent information
      if (result.first_name && result.first_name.trim() !== "") {
        setFieldValue("parentFirstName", result.first_name);
        populatedFields.add("parentFirstName");
      }
      
      if (result.middle_name && result.middle_name.trim() !== "") {
        setFieldValue("parentMiddleName", result.middle_name);
        populatedFields.add("parentMiddleName");
      }
      
      if (result.last_name && result.last_name.trim() !== "") {
        setFieldValue("parentSurname", result.last_name);
        populatedFields.add("parentSurname");
      }
      
      if (result.suffix_name && result.suffix_name.trim() !== "" && result.suffix_name !== "XX") {
        setFieldValue("parentExtension", result.suffix_name);
        populatedFields.add("parentExtension");
      }
      
      if (result.birthday && result.birthday.trim() !== "") {
        // Convert birthday format from YYYY-M-D to proper date
        const [year, month, day] = result.birthday.split('-');
        const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        setFieldValue("parentBirthday", formattedDate);
        populatedFields.add("parentBirthday");
      }
      
      if (result.gender_name && result.gender_name.trim() !== "") {
        setFieldValue("parentGender", result.gender_name);
        populatedFields.add("parentGender");
      }
      
      if (result.civil_status_name && result.civil_status_name.trim() !== "") {
        setFieldValue("parentCivilStatus", result.civil_status_name);
        populatedFields.add("parentCivilStatus");
      }
      
      if (result.email && result.email.trim() !== "") {
        setFieldValue("parentEmailAddress", result.email);
        populatedFields.add("parentEmailAddress");
      }
      
      if (result.mobile_no && result.mobile_no.trim() !== "") {
        // Clean mobile number (remove spaces and format properly)
        const cleanMobile = result.mobile_no.replace(/\s/g, '');
        setFieldValue("parentCellphone", cleanMobile);
        // Do NOT add to populatedFields so it remains editable
      }
      
      // Mark parent QC ID as populated to disable it
      populatedFields.add("parentQcId");
      
      // Store the populated fields
      setParentQcidPopulatedFields(populatedFields);
      
      notification.success({
        message: "Success",
        description: "Parent QC ID information has been loaded and form fields have been auto-populated.",
        duration: 4,
      });

    } catch (error) {
      setParentQcidData(null);
      setParentQcidPopulatedFields(new Set());

      // [SECURITY: A09] Use secure error logging - NO PII exposure
      logErrorSecurely(error, 'fetchAndUpdateParentQcidData');
      
      // [SECURITY: A04] Show generic error message - NO technical details
      const errorMessage = getErrorMessage(error);
      
      notification.error({
        message: "Error",
        description: errorMessage,
        duration: 4,
      });
    } finally {
      setIsParentQcidLoading(false);
    }
  };

  // Function to clear parent QC ID data and re-enable fields
  const clearParentQcidData = (setFieldValue) => {
    // Clear the populated fields
    parentQcidPopulatedFields.forEach(fieldName => {
      setFieldValue(fieldName, "");
    });
    
    // Clear parent QC ID field
    setFieldValue("parentQcId", "");
    
    // Reset state
    setParentQcidData(null);
    setParentQcidPopulatedFields(new Set());
    
    notification.info({
      message: "Cleared",
      description: "Parent QC ID data has been cleared. You can now fill the form manually or enter a new QC ID.",
      duration: 3,
    });
  };

  const sanitizeInput = (value) => {
    // Remove any potentially harmful characters
    return value.replace(/[^\p{L}\p{N}\s.'-]/gu, "").trim();
  };

  const validateInput = (value) => {
    // Regular expression to allow only letters (including ñ and Ñ) and spaces, but not space as the first character
    const regex = /^[a-zA-ZñÑ][a-zA-ZñÑ\s]*$/;
    return regex.test(value);
  };

  const sanitizeNumericInput = (value) => {
    // Remove any non-numeric characters
    return value.replace(/[^0-9]/g, "");
  };

  const validateNumericInput = (value) => {
    // Ensure the value is a valid number and falls within expected ranges
    const num = Number(value);
    return !Number.isNaN(num);
  };

  const handleChange = useCallback((e, setFieldValue) => {
    const value = e.target.value;
    const sanitizedValue = sanitizeInput(value);
    if (validateInput(sanitizedValue)) {
      setFieldValue(e.target.name, sanitizedValue);
    } else {
      setFieldValue(e.target.name, "");
      notification.error({
        message: "Invalid Input",
        description: "Please enter a valid input.",
        duration: 4,
      });
    }
  }, []);

  const handleChangeNumbers = (e, setFieldValue) => {
    const value = e.target.value;
    const sanitizedValue = sanitizeNumericInput(value);

    if (validateNumericInput(sanitizedValue)) {
      setFieldValue(e.target.name, sanitizedValue);
    } else {
      // Handle invalid input
      console.error("Invalid numeric input detected");
    }
  };

  const GenderDependentFields = () => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      if (values.gender === "Male") {
        setFieldValue("hasMenstruationStarted", "N/A");
        if (values.isCircumcised === "N/A") {
          setFieldValue("isCircumcised", "");
        }
      } else if (values.gender === "Female") {
        setFieldValue("isCircumcised", "N/A");
        if (values.hasMenstruationStarted === "N/A") {
          setFieldValue("hasMenstruationStarted", "");
        }
      }
    }, [values.gender, setFieldValue]);

    return null;
  };

  /**
   * Auto-scroll to first error field component
   * Detects first validation error and scrolls to it smoothly
   * Also shows notification when validation fails
   */
  const AutoScrollToError = () => {
    const { errors, touched, submitCount, isValidating, isSubmitting } = useFormikContext();
    const [lastSubmitCount, setLastSubmitCount] = useState(0);
    const [hasShownNotification, setHasShownNotification] = useState(false);

    useEffect(() => {
      // Track submit attempts
      if (submitCount > lastSubmitCount) {
        setLastSubmitCount(submitCount);
        setHasShownNotification(false);
      }
    }, [submitCount, lastSubmitCount]);

    useEffect(() => {
      // Show notification after validation completes with errors
      if (!isValidating && !isSubmitting && submitCount > 0 && !hasShownNotification) {
        if (errors && Object.keys(errors).length > 0) {
          const hasVisibleErrors = Object.keys(errors).some(key => touched[key]);
          
          if (hasVisibleErrors) {
            setHasShownNotification(true);
            
            // Show notification for incomplete form
            notification.error({
              message: "Form Incomplete",
              description: "Please fill in all required fields and correct any errors before proceeding.",
              duration: 4,
            });

            // Helper function to get nested field name from error path
            const getFirstErrorFieldName = (errors, touched) => {
              // Handle nested errors (like array fields)
              for (const key of Object.keys(errors)) {
                if (touched[key]) {
                  const error = errors[key];
                  
                  // If it's an array of errors
                  if (Array.isArray(error)) {
                    for (let i = 0; i < error.length; i++) {
                      if (error[i] && touched[key] && touched[key][i]) {
                        // Find first nested error field
                        const nestedError = error[i];
                        const nestedTouched = touched[key][i];
                        
                        for (const nestedKey of Object.keys(nestedError)) {
                          if (nestedTouched[nestedKey]) {
                            return `${key}[${i}].${nestedKey}`;
                          }
                        }
                      }
                    }
                  }
                  
                  // If it's a simple error, return the key
                  return key;
                }
              }
              return null;
            };

            const firstErrorFieldName = getFirstErrorFieldName(errors, touched);
            
            if (firstErrorFieldName) {
              // Try to find the field in the DOM with various selectors
              let errorElement = 
                document.querySelector(`[name="${firstErrorFieldName}"]`) ||
                document.querySelector(`input[name="${firstErrorFieldName}"]`) ||
                document.querySelector(`select[name="${firstErrorFieldName}"]`) ||
                document.querySelector(`textarea[name="${firstErrorFieldName}"]`) ||
                document.querySelector(`[id="${firstErrorFieldName}"]`) ||
                document.querySelector(`[data-field="${firstErrorFieldName}"]`);
              
              // If no direct element found, look for the error message itself
              // This handles checkbox/radio groups that don't have name attributes
              if (!errorElement) {
                // Find all FormHelperText elements with error messages
                const allHelperTexts = document.querySelectorAll('.MuiFormHelperText-root');
                for (const helperText of allHelperTexts) {
                  if (helperText.textContent && helperText.textContent.trim() !== '') {
                    // Check if this helper text is showing one of our errors
                    const errorMessage = errors[firstErrorFieldName];
                    if (errorMessage && helperText.textContent.includes(errorMessage)) {
                      errorElement = helperText;
                      break;
                    }
                  }
                }
              }
              
              // Final fallback: any visible error indicator
              if (!errorElement) {
                errorElement = 
                  document.querySelector('.Mui-error') ||
                  document.querySelector('[aria-invalid="true"]') ||
                  document.querySelector('.MuiFormHelperText-root');
              }
              
              if (errorElement) {
                // Scroll to error with smooth behavior - center vertically in viewport
                const elementRect = errorElement.getBoundingClientRect();
                const absoluteElementTop = elementRect.top + window.pageYOffset;
                const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
                
                window.scrollTo({ top: middle, behavior: 'smooth' });
                
                // Focus the element after scrolling if it's focusable
                setTimeout(() => {
                  if (errorElement.focus && typeof errorElement.focus === 'function') {
                    errorElement.focus({ preventScroll: true });
                  }
                }, 500);
              }
            }
          }
        }
      }
    }, [isValidating, isSubmitting, errors, touched, submitCount, hasShownNotification]);

    return null;
  };

  return (
    <Box mt={2}>
      <Formik
        initialValues={currentPersonalInformation}
        enableReinitialize={true}
        validationSchema={personalInformationValidationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            savePersonalInformation(values);
            
            await handleNext(values);
          } catch (error) {
            console.error("Error submitting form:", error);
            const errorMessage = error.response?.data?.message || error.message || "An unexpected error occurred";
            notification.error({
              message: "Error",
              description: errorMessage,
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          touched,
          values,
          setFieldValue,
          isValid,
          isSubmitting,
        }) => {
          const { childErrors, parentErrors } = countErrors(
            currentPersonalInformation,
            errors,
            touched
          );

          return (
            <Form>
              <GenderDependentFields />
              <AutoScrollToError />
              <Box>
                <Box mb={2}>
                  <div className="muiBadge-display-inline">
                    <Badge badgeContent={childErrors} color="error">
                      <Accordion
                        expanded={panel1Expanded}
                        onChange={() => {
                          setPanel1Expanded(!panel1Expanded);
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreIcon
                              style={
                                panel1Expanded
                                  ? { color: "#fff" }
                                  : { color: "#26516F" }
                              }
                            />
                          }
                          aria-controls="panel1a-content"
                          id="panel1a-header"
                          style={
                            panel1Expanded
                              ? { backgroundColor: "#26516F", color: "#fff" }
                              : { backgroundColor: "#fff", color: "#26516F" }
                          }
                        >
                          <Typography fontWeight="bold">
                            IMPORMASYON NG BATA
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {panel1Expanded && (
                            <Box p={2}>
                              <InstructionText />
                              <Grid
                                container
                                spacing={2}
                                style={{ marginBottom: 4 }}
                              >
                                <Grid item xs={12} lg={12} md={3}>
                                  <Typography mt={1}>QCID Number:</Typography>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    display="block"
                                    mb={1}
                                  >
                                    Providing QCID helps auto-populate
                                    information and speeds up processing.
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                      <Field
                                        as={TextField}
                                        name="qcId"
                                        size="small"
                                        type="text"
                                        fullWidth
                                        placeholder="Enter 14-16 digit QCID number"
                                        helperText={
                                          touched.qcId && errors.qcId
                                            ? errors.qcId
                                            : ""
                                        }
                                        error={errors.qcId && touched.qcId}
                                        onChange={(e) => handleChangeNumbers(e, setFieldValue)}
                                        disabled={qcidData || isQcidLoading}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                      {qcidData ? (
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          fullWidth
                                          onClick={() => clearQcidData(setFieldValue)}
                                          aria-label="Clear QC ID data and enable manual input"
                                          aria-busy={isQcidLoading}
                                          aria-live="polite"
                                          startIcon={<DeleteIcon />}
                                        >
                                          Clear QC ID Data
                                        </Button>
                                      ) : (
                                        <Box display="flex" alignItems="center">
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={() => fetchAndUpdateQcidData(values.qcId, setFieldValue)}
                                            disabled={!values.qcId || values.qcId.length < 14 || isQcidLoading}
                                            aria-label="Fill Up Using QC ID Data"
                                            sx={{ flex: 1 }}
                                          >
                                            {isQcidLoading ? 'Loading...' : 'Fill Up Using QC ID Data'}
                                          </Button>
                                          <Tooltip
                                            title={QCID_TOOLTIP_TEXT}
                                            enterTouchDelay={0}
                                            arrow
                                            placement="top"
                                          >
                                            <IconButton
                                              aria-label="Impormasyon tungkol sa QC ID auto-fill"
                                              sx={{ ml: 1 }}
                                            >
                                              <InfoOutlinedIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      )}
                                    </Grid>
                                  </Grid>
                                </Grid>
                                {/* Button for Fill USING QCID*/}
                              </Grid>
                              <Typography mb={1}>Pangalan ng bata: <span style={{color: 'red'}}>*</span></Typography>
                              <Box>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <Field
                                      as={TextField}
                                      name="surname"
                                      label={<>Surname <span style={{color: 'red'}}>*</span></>}
                                      size="small"
                                      fullWidth
                                      helperText={
                                        touched.surname && errors.surname
                                      }
                                      error={errors.surname && touched.surname}
                                      onChange={(e) =>
                                        handleChange(e, setFieldValue)
                                      }
                                      disabled={qcidPopulatedFields.has('surname')}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Field
                                      as={TextField}
                                      name="firstName"
                                      label={<>First name <span style={{color: 'red'}}>*</span></>}
                                      size="small"
                                      fullWidth
                                      helperText={
                                        touched.firstName && errors.firstName
                                      }
                                      error={
                                        errors.firstName && touched.firstName
                                      }
                                      onChange={(e) =>
                                        handleChange(e, setFieldValue)
                                      }
                                      disabled={qcidPopulatedFields.has('firstName')}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Field
                                      as={TextField}
                                      name="middleName"
                                      label="Middle name (Optional)"
                                      size="small"
                                      fullWidth
                                      helperText={
                                        touched.middleName && errors.middleName
                                      }
                                      error={
                                        errors.middleName && touched.middleName
                                      }
                                      disabled={qcidPopulatedFields.has('middleName')}
                                    />
                                  </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Typography mt={1}>
                                      Kaarawan ng bata: <span style={{color: 'red'}}>*</span>
                                    </Typography>
                                    <LocalizationProvider
                                      dateAdapter={AdapterDayjs}
                                    >
                                      <DatePicker
                                        name="childBirthday"
                                        value={dayjs(values.childBirthday)}
                                        onChange={(value) => {
                                          setFieldValue("childBirthday", value);
                                        }}
                                        maxDate={dayjs()}
                                        disabled={qcidPopulatedFields.has('childBirthday')}
                                        slotProps={{
                                          textField: {
                                            fullWidth: true,
                                            size: "small",
                                            helperText:
                                              touched.childBirthday &&
                                              errors.childBirthday,
                                            error: Boolean(
                                              errors.childBirthday &&
                                                touched.childBirthday
                                            ),
                                            disabled: qcidPopulatedFields.has('childBirthday'),
                                          },
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography mt={1}>Kasarian: <span style={{color: 'red'}}>*</span></Typography>
                                    <FormControl fullWidth size="small">
                                      <Field
                                        as={Select}
                                        name="gender"
                                        helperText={
                                          touched.gender && errors.gender
                                        }
                                        error={touched.gender && errors.gender}
                                        disabled={qcidPopulatedFields.has('gender')}
                                      >
                                        <MenuItem value={"Male"}>
                                          Lalaki
                                        </MenuItem>
                                        <MenuItem value={"Female"}>
                                          Babae
                                        </MenuItem>
                                      </Field>
                                      <FormHelperText>
                                        {touched.gender && errors.gender}
                                      </FormHelperText>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={3}>
                                    <Typography mt={1}>Relihiyon: <span style={{color: 'red'}}>*</span></Typography>
                                    <FormControl fullWidth size="small" error>
                                      <Field
                                        as={Select}
                                        name="religion"
                                        helperText={
                                          touched.religion && errors.religion
                                        }
                                        error={Boolean(
                                          touched.religion && errors.religion
                                        )}
                                      >
                                        <MenuItem value={"Roman Catholic"}>
                                          Roman Catholic
                                        </MenuItem>
                                        <MenuItem value={"Christian"}>
                                          Christian
                                        </MenuItem>
                                        <MenuItem value={"Protestant"}>
                                          Protestant
                                        </MenuItem>
                                        <MenuItem value={"Ind. Church"}>
                                          Ind. Church
                                        </MenuItem>
                                        <MenuItem value={"Islam"}>
                                          Islam
                                        </MenuItem>
                                        <MenuItem value={"Iglesia ni Cristo"}>
                                          Iglesia ni Cristo
                                        </MenuItem>
                                        {/* <MenuItem value={7}>Iba pa</MenuItem> */}
                                      </Field>
                                      <FormHelperText>
                                        {touched.religion && errors.religion}
                                      </FormHelperText>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography mt={1}>
                                      Blood Type:
                                      <span
                                        style={{
                                          color: "#888",
                                          fontWeight: 400,
                                          fontSize: "0.95em",
                                          marginLeft: 4,
                                        }}
                                      >
                                        (Optional)
                                      </span>
                                    </Typography>
                                    <FormControl fullWidth size="small">
                                      <Field
                                        as={Select}
                                        name="bloodType"
                                        displayEmpty
                                        inputProps={{
                                          "aria-label": "Blood Type",
                                        }}
                                      >
                                        <MenuItem value="">
                                          <em>
                                            -- Select Blood Type (Optional) --
                                          </em>
                                        </MenuItem>
                                        {bloodTypeOptions.map((bloodType) => (
                                          <MenuItem
                                            key={bloodType.value}
                                            value={bloodType.value}
                                          >
                                            {bloodType.label}
                                          </MenuItem>
                                        ))}
                                      </Field>
                                      {/* <FormHelperText>
                                        This field is optional. If you know your blood type, please select it.
                                      </FormHelperText> */}
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography mt={1}>Height(cm): <span style={{color: 'red'}}>*</span></Typography>
                                    <Field
                                      as={TextField}
                                      name="height"
                                      size="small"
                                      type="number"
                                      fullWidth
                                      inputProps={{
                                        step: 0.01,
                                        min: 20,
                                        max: 300
                                      }}
                                      helperText={
                                        touched.height && errors.height
                                      }
                                      error={errors.height && touched.height}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography mt={1}>Weight(kg): <span style={{color: 'red'}}>*</span></Typography>
                                    <Field
                                      as={TextField}
                                      name="weight"
                                      size="small"
                                      type="number"
                                      fullWidth
                                      inputProps={{
                                        step: 0.01,
                                        min: 2,
                                        max: 150
                                      }}
                                      helperText={
                                        touched.weight && errors.weight
                                      }
                                      error={errors.weight && touched.weight}
                                    />
                                  </Grid>
                                </Grid>
                                <Typography my={1}>Tirahan: <span style={{color: 'red'}}>*</span></Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <Field
                                      as={TextField}
                                      name="houseNo"
                                      label={<>House # <span style={{color: 'red'}}>*</span></>}
                                      size="small"
                                      fullWidth
                                      helperText={
                                        touched.houseNo && errors.houseNo
                                      }
                                      error={errors.houseNo && touched.houseNo}
                                      disabled={qcidPopulatedFields.has('houseNo')}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Field
                                      as={TextField}
                                      name="blkAndLot"
                                      label="Blk & Lot"
                                      size="small"
                                      fullWidth
                                      helperText={
                                        touched.blkAndLot && errors.blkAndLot
                                      }
                                      error={
                                        errors.blkAndLot && touched.blkAndLot
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Field
                                      as={TextField}
                                      name="street"
                                      label={<>Street <span style={{color: 'red'}}>*</span></>}
                                      size="small"
                                      fullWidth
                                      helperText={
                                        touched.street && errors.street
                                      }
                                      error={errors.street && touched.street}
                                      disabled={qcidPopulatedFields.has('street')}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Barangay <span style={{color: 'red'}}>*</span></InputLabel>
                                      <Field
                                        as={Select}
                                        name="barangay"
                                        label="Barangay"
                                        disabled={qcidPopulatedFields.has('barangay')}
                                        onChange={(e) => {
                                          const selectedBarangay =
                                            e.target.value;
                                          setFieldValue(
                                            "barangay",
                                            selectedBarangay
                                          );

                                          // Check if Non-QC Residents is selected
                                          if (selectedBarangay === "Non-QC Residents") {
                                            setFieldValue("zipCode", ""); // Clear zip code
                                            setFieldValue("district", ""); // Clear district for Non-QC Residents
                                          } else if (barangayData[selectedBarangay]) {
                                            // Check the mapping for QC barangays
                                            const { zipCode, district } =
                                              barangayData[selectedBarangay];
                                            setFieldValue("zipCode", zipCode); // Set postal code
                                            setFieldValue("district", district); // Set district
                                          } else {
                                            setFieldValue("zipCode", ""); // Clear if not found
                                            setFieldValue("district", ""); // Clear if not found
                                          }
                                        }}
                                        helperText={
                                          touched.barangay && errors.barangay
                                        }
                                        error={
                                          touched.barangay && errors.barangay
                                        }
                                      >
                                        <MenuItem key={0} value="none" disabled>
                                          NONE
                                        </MenuItem>
                                          {barangayList.length > 0 &&
                                            barangayList.map((barangay) => (
                                              <MenuItem
                                                key={barangay.value}
                                                value={barangay.value}
                                              >
                                                {barangay.label}
                                              </MenuItem>
                                            ))}
                                          <MenuItem key="non-qc-residents" value="Non-QC Residents">
                                            Non-QC Residents
                                          </MenuItem>
                                      </Field>
                                      <FormHelperText>
                                        {touched.barangay && errors.barangay}
                                      </FormHelperText>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Field
                                      as={TextField}
                                      fullWidth
                                      size="small"
                                      name="district"
                                      label={
                                        values.barangay === 'Non-QC Residents' 
                                          ? 'District' 
                                          : <>District <span style={{color: 'red'}}>*</span></>
                                      }
                                      value={values.district}
                                      InputProps={{
                                        readOnly: values.barangay !== 'Non-QC Residents',
                                      }}
                                      disabled={values.barangay !== 'Non-QC Residents'}
                                      error={
                                        touched.district &&
                                        Boolean(errors.district)
                                      }
                                      helperText={
                                        touched.district && errors.district
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Field
                                      as={TextField}
                                      fullWidth
                                      size="small"
                                      name="zipCode"
                                      label="Zip Code"
                                      value={values.zipCode}
                                      error={
                                        touched.zipCode &&
                                        Boolean(errors.zipCode)
                                      }
                                      helperText={
                                        touched.zipCode && errors.zipCode
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Field
                                      as={TextField}
                                      name="city"
                                      label={<>City <span style={{color: 'red'}}>*</span></>}
                                      size="small"
                                      fullWidth
                                      helperText={touched.city && errors.city}
                                      error={errors.city && touched.city}
                                      disabled={qcidPopulatedFields.has('city')}
                                    />
                                  </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={12}>
                                    <Typography mt={1}>
                                      Impormasyon sa Pag-aaral: <span style={{color: 'red'}}>*</span>
                                    </Typography>
                                    <Field
                                      as={RadioGroup}
                                      name="educationalStatus"
                                      onChange={(e) => {
                                        const selectedValue = e.target.value;
                                        setFieldValue(
                                          "educationalStatus",
                                          selectedValue
                                        );
                                        if (
                                          selectedValue !==
                                          values.educationalStatus
                                        ) {
                                          educationRelatedFields.forEach(
                                            (field) => {
                                              setFieldValue(
                                                field,
                                                field === "reasonForNotStudying"
                                                  ? []
                                                  : ""
                                              );
                                            }
                                          );
                                        }
                                      }}
                                    >
                                      <FormControlLabel
                                        value="studying"
                                        control={<Radio size="small" />}
                                        label="Nag-aaral"
                                      />
                                      <FormControlLabel
                                        value="stopped"
                                        control={<Radio size="small" />}
                                        label="Tumigil sa pag-aaral"
                                      />
                                      <FormControlLabel
                                        value="never_attended_school"
                                        control={<Radio size="small" />}
                                        label="Hindi pa nakakapag-aaral"
                                      />
                                      <FormHelperText>
                                        {touched.educationalStatus &&
                                          errors.educationalStatus}
                                      </FormHelperText>
                                    </Field>
                                    {values.educationalStatus ===
                                      "studying" && (
                                      <Box>
                                        <Box mb={1}>
                                          <FormControl fullWidth size="small">
                                            <InputLabel>
                                              Education Level <span style={{color: 'red'}}>*</span>
                                            </InputLabel>
                                            <Field
                                              as={Select}
                                              name="educationLevel"
                                              label="Education Level"
                                              helperText={
                                                touched.educationLevel &&
                                                errors.educationLevel
                                              }
                                              error={
                                                touched.educationLevel &&
                                                errors.educationLevel
                                              }
                                            >
                                              <MenuItem value={"elementary"}>
                                                Elementary
                                              </MenuItem>
                                              <MenuItem value={"high school"}>
                                                High School
                                              </MenuItem>
                                              <MenuItem value={"sped"}>
                                                SPED
                                              </MenuItem>
                                            </Field>
                                            <FormHelperText>
                                              {touched.educationLevel &&
                                                errors.educationLevel}
                                            </FormHelperText>
                                          </FormControl>
                                        </Box>{" "}
                                        <Box>
                                          {values.educationLevel !== "" && (
                                            <>
                                              <Box mb={1}>
                                                <Typography>
                                                  School name <span style={{color: 'red'}}>*</span>
                                                </Typography>
                                                <FormControl
                                                  fullWidth
                                                  size="small"
                                                  error={
                                                    touched.schoolName &&
                                                    Boolean(errors.schoolName)
                                                  }
                                                >
                                                  <Autocomplete
                                                    id="schoolName"
                                                    options={(() => {
                                                      let options = [];
                                                      if (
                                                        values.educationLevel ===
                                                        "elementary"
                                                      ) {
                                                        options =
                                                          renderSortedSchoolOptions(
                                                            elementaryOptions
                                                          );
                                                      } else if (
                                                        values.educationLevel ===
                                                        "high school"
                                                      ) {
                                                        options =
                                                          renderSortedSchoolOptions(
                                                            secondaryOptions
                                                          );
                                                      } else if (
                                                        values.educationLevel ===
                                                        "sped"
                                                      ) {
                                                        options =
                                                          renderSortedSchoolOptions(
                                                            [
                                                              ...elementaryOptions,
                                                              ...secondaryOptions,
                                                            ]
                                                          );
                                                      }
                                                      // Ensure "Iba pa" is always at the end
                                                      return [
                                                        ...options,
                                                        "Iba pa",
                                                      ];
                                                    })()}
                                                    value={
                                                      values.schoolName || null
                                                    }
                                                    onChange={(_, newValue) => {
                                                      setFieldValue(
                                                        "schoolName",
                                                        newValue || ""
                                                      );
                                                      // Clear specificSchoolName if not "Iba pa"
                                                      if (
                                                        newValue !== "Iba pa"
                                                      ) {
                                                        setFieldValue(
                                                          "specificSchoolName",
                                                          ""
                                                        );
                                                      }
                                                    }}
                                                    renderInput={(params) => (
                                                      <TextField
                                                        {...params}
                                                        name="schoolName"
                                                        size="small"
                                                        error={
                                                          touched.schoolName &&
                                                          Boolean(
                                                            errors.schoolName
                                                          )
                                                        }
                                                        helperText={
                                                          touched.schoolName &&
                                                          errors.schoolName
                                                        }
                                                      />
                                                    )}
                                                    disablePortal={false}
                                                    openOnFocus
                                                    fullWidth
                                                  />
                                                </FormControl>
                                              </Box>

                                              {values.schoolName ===
                                                "Iba pa" && (
                                                <Box mb={1}>
                                                  <Field
                                                    as={TextField}
                                                    name="specificSchoolName"
                                                    label="Please specify school name"
                                                    size="small"
                                                    fullWidth
                                                    helperText={
                                                      touched.specificSchoolName &&
                                                      errors.specificSchoolName
                                                    }
                                                    error={Boolean(
                                                      touched.specificSchoolName &&
                                                        errors.specificSchoolName
                                                    )}
                                                  />
                                                </Box>
                                              )}

                                              <Typography>
                                                Grade level <span style={{color: 'red'}}>*</span>
                                              </Typography>
                                              <FormControl
                                                fullWidth
                                                size="small"
                                              >
                                                <Field
                                                  as={Select}
                                                  name="gradeLevel"
                                                  helperText={
                                                    touched.gradeLevel &&
                                                    errors.gradeLevel
                                                  }
                                                  error={
                                                    touched.gradeLevel &&
                                                    errors.gradeLevel
                                                  }
                                                >
                                                  {gradeLevelOptions.map(
                                                    (item) => (
                                                      <MenuItem
                                                        key={item}
                                                        value={item}
                                                      >
                                                        {item}
                                                      </MenuItem>
                                                    )
                                                  )}
                                                </Field>
                                                <FormHelperText>
                                                  {touched.gradeLevel &&
                                                    errors.gradeLevel}
                                                </FormHelperText>
                                              </FormControl>
                                            </>
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                    {values.educationalStatus === "stopped" && (
                                      <Box>
                                        <Box mb={1}>
                                          <Field
                                            as={TextField}
                                            name="lastYearOfStudy"
                                            label={<>Huling taon ng pag-aaral <span style={{color: 'red'}}>*</span></>}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            helperText={
                                              touched.lastYearOfStudy &&
                                              errors.lastYearOfStudy
                                            }
                                            error={
                                              touched.lastYearOfStudy &&
                                              errors.lastYearOfStudy
                                            }
                                          />
                                        </Box>
                                      </Box>
                                    )}
                                    {values.educationalStatus === "stopped" && (
                                      <CheckboxOptions
                                        options={stoppingStudyReasonsOptions}
                                        name="reasonForNotStudying"
                                        label={<>Dahilan ng hindi pag-aaral <span style={{color: 'red'}}>*</span></>}
                                      />
                                    )}
                                    {values.educationalStatus ===
                                      "never_attended_school" && (
                                      <CheckboxOptions
                                        options={notStudyingReasonOptions}
                                        name="reasonForNotStudying"
                                        label={<>Dahilan ng hindi pag-aaral <span style={{color: 'red'}}>*</span></>}
                                      />
                                    )}
                                    <Typography mt={2}>
                                      Mga Karagdagang Impormasyon ng Bata:
                                    </Typography>
                                    <Grid container>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography mt={2}>
                                            Nireregla na ba? <span style={{color: 'red'}}>*</span>
                                          </Typography>
                                          <Field
                                            as={RadioGroup}
                                            name="hasMenstruationStarted"
                                            aria-label="Menstruation status"
                                          >
                                            <FormControlLabel
                                              value="Yes"
                                              control={<Radio size="small" />}
                                              label="Oo"
                                              disabled={
                                                values.gender === "Male"
                                              }
                                            />
                                            <FormControlLabel
                                              value="No"
                                              control={<Radio size="small" />}
                                              label="Hindi"
                                              disabled={
                                                values.gender === "Male"
                                              }
                                            />
                                            <FormControlLabel
                                              value="N/A"
                                              control={<Radio size="small" />}
                                              label="Not applicable"
                                              disabled={
                                                values.gender === "Female"
                                              }
                                            />
                                            <FormHelperText>
                                              {touched.hasMenstruationStarted &&
                                                errors.hasMenstruationStarted}
                                            </FormHelperText>
                                          </Field>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                          <Typography mt={2}>
                                            Natuli na ba? <span style={{color: 'red'}}>*</span>
                                          </Typography>
                                          <Field
                                            as={RadioGroup}
                                            name="isCircumcised"
                                            aria-label="Is Circumcised"
                                          >
                                            <FormControlLabel
                                              value="Yes"
                                              control={<Radio size="small" />}
                                              label="Oo"
                                              disabled={
                                                values.gender === "Female"
                                              }
                                            />
                                            <FormControlLabel
                                              value="No"
                                              control={<Radio size="small" />}
                                              label="Hindi"
                                              disabled={
                                                values.gender === "Female"
                                              }
                                            />
                                            <FormControlLabel
                                              value="N/A"
                                              control={<Radio size="small" />}
                                              label="Not applicable"
                                              disabled={
                                                values.gender === "Male"
                                              }
                                            />
                                            <FormHelperText>
                                              {touched.isCircumcised &&
                                                errors.isCircumcised}
                                            </FormHelperText>
                                          </Field>
                                        </Grid>
                                      </Grid>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Typography mt={2}>
                                            Nagpapakita ng interes sa arts? <span style={{color: 'red'}}>*</span>
                                          </Typography>
                                          <Field
                                            as={RadioGroup}
                                            name="isInterestedInArts"
                                          >
                                            <FormControlLabel
                                              value="Yes"
                                              control={<Radio size="small" />}
                                              label="Oo"
                                            />
                                            <FormControlLabel
                                              value="No"
                                              control={<Radio size="small" />}
                                              label="Hindi"
                                            />
                                            <FormHelperText>
                                              {touched.isInterestedInArts &&
                                                errors.isInterestedInArts}
                                            </FormHelperText>
                                          </Field>
                                        </Grid>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Typography mt={2}>
                                          Marunong sa mga gawaing bahay? <span style={{color: 'red'}}>*</span>
                                        </Typography>
                                        <Field
                                          as={RadioGroup}
                                          name="isSkilledInHouseholdChores"
                                          onChange={(event) => {
                                            setFieldValue(
                                              "householdChoresChildCanDo",
                                              []
                                            );
                                            setFieldValue(
                                              "isSkilledInHouseholdChores",
                                              event.target.value
                                            );
                                          }}
                                        >
                                          <FormControlLabel
                                            value="Yes"
                                            control={<Radio size="small" />}
                                            label="Oo"
                                          />
                                          <FormControlLabel
                                            value="No"
                                            control={<Radio size="small" />}
                                            label="Hindi"
                                          />
                                          <FormHelperText>
                                            {touched.isSkilledInHouseholdChores &&
                                              errors.isSkilledInHouseholdChores}
                                          </FormHelperText>
                                        </Field>
                                      </Grid>
                                    </Grid>
                                    {values.isSkilledInHouseholdChores ===
                                      "Yes" && (
                                      <>
                                        <Typography mt={2}>
                                          Anu-anong mga gawaing bahay ang kaya
                                          nang gawin ng bata? (piliin lahat ng
                                          naayon)
                                        </Typography>
                                        {householdChoresChildCanDoOptions.map(
                                          (option) => (
                                            <div key={option.value}>
                                              <FormControlLabel
                                                control={
                                                  <Field
                                                    type="checkbox"
                                                    name="householdChoresChildCanDo"
                                                    value={option.value}
                                                    as={Checkbox}
                                                    checked={values.householdChoresChildCanDo.includes(
                                                      option.value
                                                    )}
                                                    onChange={() => {
                                                      const currentValues =
                                                        values.householdChoresChildCanDo;
                                                      const newValues =
                                                        currentValues.includes(
                                                          option.value
                                                        )
                                                          ? currentValues.filter(
                                                              (value) =>
                                                                value !==
                                                                option.value
                                                            )
                                                          : [
                                                              ...currentValues,
                                                              option.value,
                                                            ];
                                                      setFieldValue(
                                                        "householdChoresChildCanDo",
                                                        newValues
                                                      );
                                                    }}
                                                  />
                                                }
                                                label={option.label}
                                              />
                                            </div>
                                          )
                                        )}
                                      </>
                                    )}
                                    <FormHelperText>
                                      {touched.householdChoresChildCanDo &&
                                        errors.householdChoresChildCanDo}
                                    </FormHelperText>

                                    <Typography mt={2}>
                                      Mayroong Certificate of Disability? <span style={{color: 'red'}}>*</span>
                                    </Typography>
                                    <Field
                                      as={RadioGroup}
                                      name="certificateOfDisability"
                                    >
                                      <FormControlLabel
                                        value="Meron"
                                        control={<Radio size="small" />}
                                        label="Meron"
                                      />
                                      <FormControlLabel
                                        value="Wala"
                                        control={<Radio size="small" />}
                                        label="Wala"
                                      />
                                      <FormHelperText>
                                        {touched.certificateOfDisability &&
                                          errors.certificateOfDisability}
                                      </FormHelperText>
                                    </Field>
                                    {values.certificateOfDisability ===
                                      "Meron" && (
                                      <Box mb={2}>
                                        <LocalizationProvider
                                          dateAdapter={AdapterDayjs}
                                        >
                                          <DatePicker
                                            name="lastCheckUp"
                                            label={<>Huling check-up <span style={{color: 'red'}}>*</span></>}
                                            value={values.lastCheckUp}
                                            onChange={(value) =>
                                              setFieldValue(
                                                "lastCheckUp",
                                                value
                                              )
                                            }
                                            slotProps={{
                                              textField: {
                                                fullWidth: true,
                                                size: "small",
                                                helperText:
                                                  touched.lastCheckUp &&
                                                  errors.lastCheckUp,
                                                error: Boolean(
                                                  errors.lastCheckUp &&
                                                    touched.lastCheckUp
                                                ),
                                              },
                                            }}
                                          />
                                        </LocalizationProvider>
                                        {values.certificateOfDisabilityImage && (
                                          <Box>
                                            <Grid container spacing={2}>
                                              <Grid item xs={12} md={8}>
                                                {isConvertingImage ? (
                                                  <Box
                                                    sx={{
                                                      width: "100%",
                                                      marginTop: "10px",
                                                      padding: "20px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    <Typography variant="body2" color="textSecondary">
                                                      Converting image for preview...
                                                    </Typography>
                                                  </Box>
                                                ) : (
                                                  <img
                                                    src={certificateImagePreview || URL.createObjectURL(values.certificateOfDisabilityImage)}
                                                    alt="Certificate of Disability"
                                                    style={{
                                                      width: "100%",
                                                      marginTop: "10px",
                                                    }}
                                                    onError={(e) => {
                                                      console.error('Image preview error:', e);
                                                      e.target.style.display = 'none';
                                                    }}
                                                  />
                                                )}
                                              </Grid>
                                            </Grid>
                                          </Box>
                                        )}
                                        <input
                                          accept=".jpg,.jpeg,.png,.heif,.heic,.hei"
                                          style={{ display: "none" }}
                                          id="raised-button-file1"
                                          type="file"
                                          onChange={async (event) => {
                                            const file = event.currentTarget.files[0];
                                            
                                            if (file) {
                                              // Validate file extension
                                              if (!validateFileExtension(file)) {
                                                notification.error({
                                                  message: 'Invalid File Type',
                                                  description: 'Please upload only image files (.jpg, .jpeg, .png, .heif, .heic, .hei)',
                                                  duration: 5
                                                });
                                                // Clear the input
                                                event.currentTarget.value = '';
                                                setFieldValue('certificateOfDisabilityImage', null);
                                                setCertificateImagePreview(null);
                                                return;
                                              }
                                              
                                              // Check if file is HEIC/HEIF and convert it
                                              const isHeic = file.name.toLowerCase().match(/\.(heic|heif|hei)$/);
                                              
                                              if (isHeic) {
                                                try {
                                                  setIsConvertingImage(true);
                                                  
                                                  // Convert HEIC to JPEG
                                                  const convertedBlob = await heic2any({
                                                    blob: file,
                                                    toType: "image/jpeg",
                                                    quality: 0.9
                                                  });
                                                  
                                                  // Create preview URL from converted blob
                                                  const previewUrl = URL.createObjectURL(convertedBlob);
                                                  setCertificateImagePreview(previewUrl);
                                                  
                                                  setIsConvertingImage(false);
                                                  
                                                  notification.success({
                                                    message: 'HEIC Image Converted',
                                                    description: 'Your HEIC image has been converted and is ready for preview.',
                                                    duration: 3
                                                  });
                                                } catch (error) {
                                                  console.error('HEIC conversion error:', error);
                                                  setIsConvertingImage(false);
                                                  setCertificateImagePreview(null);
                                                  
                                                  notification.warning({
                                                    message: 'Preview Not Available',
                                                    description: 'Could not convert HEIC image for preview, but the file will still be uploaded.',
                                                    duration: 4
                                                  });
                                                }
                                              } else {
                                                // For non-HEIC images, clear any previous preview
                                                setCertificateImagePreview(null);
                                              }
                                              
                                              setFieldValue(
                                                "certificateOfDisabilityImage",
                                                file
                                              );
                                            }
                                          }}
                                        />
                                        <label htmlFor="raised-button-file1">
                                          <Button
                                            style={{ marginTop: "10px" }}
                                            variant="contained"
                                            component="span"
                                          >
                                            Upload
                                          </Button>
                                        </label>
                                        {errors.certificateOfDisabilityImage &&
                                          touched.certificateOfDisabilityImage && (
                                            <FormHelperText>
                                              {
                                                errors.certificateOfDisabilityImage
                                              }
                                            </FormHelperText>
                                          )}
                                      </Box>
                                    )}
                                    <Typography mt={2}>
                                      PWD ID: (Isulat ang ID Number kung meron) <span style={{color: 'red'}}>*</span>
                                    </Typography>
                                    <Field as={RadioGroup} name="pwdIdStatus">
                                      <FormControlLabel
                                        value="Meron"
                                        control={<Radio size="small" />}
                                        label="Meron"
                                      />
                                      <FormControlLabel
                                        value="Wala"
                                        control={<Radio size="small" />}
                                        label="Wala"
                                      />
                                      <FormHelperText>
                                        {touched.pwdIdStatus &&
                                          errors.pwdIdStatus}
                                      </FormHelperText>
                                    </Field>
                                    {values.pwdIdStatus === "Meron" && (
                                      <Box>
                                        <Field
                                          as={TextField}
                                          name="pwdIdNumber"
                                          label={<>ID number <span style={{color: 'red'}}>*</span></>}
                                          size="small"
                                          type="number"
                                          fullWidth
                                          helperText={
                                            touched.pwdIdNumber &&
                                            errors.pwdIdNumber
                                          }
                                          error={
                                            touched.pwdIdNumber &&
                                            errors.pwdIdNumber
                                          }
                                        />
                                      </Box>
                                    )}
                                  </Grid>
                                </Grid>
                              </Box>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Badge>
                  </div>
                </Box>
                <div className="muiBadge-display-inline">
                  <Badge badgeContent={parentErrors} color="error">
                    <Accordion
                      expanded={panel2Expanded}
                      onChange={() => {
                        setPanel2Expanded(!panel2Expanded);
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            style={
                              panel2Expanded
                                ? { color: "#fff" }
                                : { color: "#26516F" }
                            }
                          />
                        }
                        aria-controls="panel2a-content"
                        id="panel2a-header"
                        style={
                          panel2Expanded
                            ? { backgroundColor: "#26516F", color: "#fff" }
                            : { backgroundColor: "#fff", color: "#26516F" }
                        }
                      >
                        <Typography fontWeight="bold">
                          IMPORMASYON NG MAGULANG O GUARDIAN
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {panel2Expanded && (
                          <Box p={2}>
                            <InstructionText />
                            {/* QC ID Section at the top */}
                            <Grid
                              container
                              spacing={2}
                              style={{ marginBottom: 24 }}
                            >
                              <Grid item xs={12} lg={12} md={12}>
                                <Typography mt={1}>QCID Number:</Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  display="block"
                                  mb={1}
                                >
                                  Providing QCID helps auto-populate
                                  information and speeds up processing.
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Field
                                      as={TextField}
                                      name="parentQcId"
                                      label="QCID number (14-16 digits)"
                                      placeholder="Enter QCID number"
                                      size="small"
                                      type="text"
                                      fullWidth
                                      disabled={parentQcidPopulatedFields.has("parentQcId")}
                                      inputProps={{
                                        maxLength: 16,
                                        pattern: "[0-9]*",
                                        inputMode: "numeric",
                                      }}
                                      onInput={(e) => {
                                        e.target.value = e.target.value.replace(
                                          /[^0-9]/g,
                                          ""
                                        );
                                      }}
                                      helperText={
                                        touched.parentQcId && errors.parentQcId
                                          ? errors.parentQcId
                                          : ""
                                      }
                                      error={
                                        touched.parentQcId && errors.parentQcId
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    {parentQcidPopulatedFields.size > 0 ? (
                                      <Button
                                        variant="outlined"
                                        color="secondary"
                                        fullWidth
                                        sx={{ height: '40px' }}
                                        onClick={() => clearParentQcidData(setFieldValue)}
                                        disabled={isParentQcidLoading}
                                      >
                                        Clear QC ID Data
                                      </Button>
                                    ) : (
                                      <Box display="flex" alignItems="center">
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          fullWidth
                                          sx={{ height: '40px', flex: 1 }}
                                          disabled={
                                            !values.parentQcId || 
                                            values.parentQcId.length < 14 || 
                                            isParentQcidLoading
                                          }
                                          onClick={() => fetchAndUpdateParentQcidData(values.parentQcId, setFieldValue)}
                                          aria-label="Fill Up Using QC ID Data"
                                        >
                                          {isParentQcidLoading ? 'Loading...' : 'Fill Up Using QC ID Data'}
                                        </Button>
                                        <Tooltip
                                          title={QCID_TOOLTIP_TEXT}
                                          enterTouchDelay={0}
                                          arrow
                                          placement="top"
                                        >
                                          <IconButton
                                            aria-label="Impormasyon tungkol sa QC ID auto-fill"
                                            sx={{ ml: 1 }}
                                          >
                                            <InfoOutlinedIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Typography mb={1}>
                              Pangalan ng magulang o guardian: <span style={{color: 'red'}}>*</span>
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Field
                                  as={TextField}
                                  name="parentSurname"
                                  label={<>Surname <span style={{color: 'red'}}>*</span></>}
                                  size="small"
                                  fullWidth
                                  disabled={parentQcidPopulatedFields.has("parentSurname")}
                                  helperText={
                                    touched.parentSurname &&
                                    errors.parentSurname
                                  }
                                  error={
                                    errors.parentSurname &&
                                    touched.parentSurname
                                  }
                                  onChange={(e) =>
                                    handleChange(e, setFieldValue)
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Field
                                  as={TextField}
                                  name="parentFirstName"
                                  label={<>First Name <span style={{color: 'red'}}>*</span></>}
                                  size="small"
                                  fullWidth
                                  disabled={parentQcidPopulatedFields.has("parentFirstName")}
                                  helperText={
                                    touched.parentFirstName &&
                                    errors.parentFirstName
                                  }
                                  error={
                                    errors.parentFirstName &&
                                    touched.parentFirstName
                                  }
                                  onChange={(e) =>
                                    handleChange(e, setFieldValue)
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Field
                                  as={TextField}
                                  name="parentMiddleName"
                                  label={<>Middle Name (Optional)</>}
                                  size="small"
                                  fullWidth
                                  disabled={parentQcidPopulatedFields.has("parentMiddleName")}
                                  helperText={
                                    touched.parentMiddleName &&
                                    errors.parentMiddleName
                                  }
                                  error={
                                    errors.parentMiddleName &&
                                    touched.parentMiddleName
                                  }
                                  onChange={(e) =>
                                    handleChange(e, setFieldValue)
                                  }
                                />
                              </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography mt={1}>
                                  Kaarawan ng magulang o guardian: <span style={{color: 'red'}}>*</span>
                                </Typography>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <DatePicker
                                    name="parentBirthday"
                                    value={dayjs(values.parentBirthday)}
                                    onChange={(value) =>
                                      setFieldValue("parentBirthday", value)
                                    }
                                    maxDate={dayjs()}
                                    disabled={parentQcidPopulatedFields.has("parentBirthday")}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        size: "small",
                                        disabled: parentQcidPopulatedFields.has("parentBirthday"),
                                        helperText:
                                          touched.parentBirthday &&
                                          errors.parentBirthday,
                                        error: Boolean(
                                          errors.parentBirthday &&
                                            touched.parentBirthday
                                        ),
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography mt={1}>Kasarian: <span style={{color: 'red'}}>*</span></Typography>
                                <FormControl fullWidth size="small">
                                  <Field
                                    as={Select}
                                    name="parentGender"
                                    disabled={parentQcidPopulatedFields.has("parentGender")}
                                    helperText={
                                      touched.parentGender &&
                                      errors.parentGender
                                    }
                                    error={
                                      touched.parentGender &&
                                      errors.parentGender
                                    }
                                  >
                                    <MenuItem value={"Male"}>Lalaki</MenuItem>
                                    <MenuItem value={"Female"}>Babae</MenuItem>
                                  </Field>
                                  <FormHelperText>
                                    {touched.parentGender &&
                                      errors.parentGender}
                                  </FormHelperText>
                                </FormControl>
                              </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography mt={1}>Relihiyon: <span style={{color: 'red'}}>*</span></Typography>
                                <FormControl fullWidth size="small" error>
                                  <Field
                                    as={Select}
                                    name="parentReligion"
                                    helperText={
                                      touched.parentReligion &&
                                      errors.parentReligion
                                    }
                                    error={Boolean(
                                      touched.parentReligion &&
                                        errors.parentReligion
                                    )}
                                  >
                                    <MenuItem value={"Roman Catholic"}>
                                      Roman Catholic
                                    </MenuItem>
                                    <MenuItem value={"Christian"}>
                                      Christian
                                    </MenuItem>
                                    <MenuItem value={"Protestant"}>
                                      Protestant
                                    </MenuItem>
                                    <MenuItem value={"Ind. Church"}>
                                      Ind. Church
                                    </MenuItem>
                                    <MenuItem value={"Islam"}>Islam</MenuItem>
                                    <MenuItem value={"Iglesia ni Cristo"}>
                                      Iglesia ni Cristo
                                    </MenuItem>
                                  </Field>
                                  <FormHelperText>
                                    {touched.parentReligion &&
                                      errors.parentReligion}
                                  </FormHelperText>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography mt={1}>Civil Status:</Typography>
                                <FormControl fullWidth size="small">
                                  <Field
                                    as={Select}
                                    name="parentCivilStatus"
                                    disabled={parentQcidPopulatedFields.has("parentCivilStatus")}
                                    helperText={
                                      touched.parentCivilStatus &&
                                      errors.parentCivilStatus
                                    }
                                    error={
                                      touched.parentCivilStatus &&
                                      errors.parentCivilStatus
                                    }
                                  >
                                    {civilStatusOptions.map((status) => (
                                      <MenuItem
                                        key={status.value}
                                        value={status.value}
                                      >
                                        {status.label}
                                      </MenuItem>
                                    ))}
                                  </Field>
                                  <FormHelperText>
                                    {touched.parentCivilStatus &&
                                      errors.parentCivilStatus}
                                  </FormHelperText>
                                </FormControl>
                              </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Typography mt={1}>
                                  Blood Type:
                                  <span
                                    style={{
                                      color: "#888",
                                      fontWeight: 400,
                                      fontSize: "0.95em",
                                      marginLeft: 4,
                                    }}
                                  >
                                    (Optional)
                                  </span>
                                </Typography>
                                <FormControl
                                  fullWidth
                                  size="small"
                                  error={
                                    errors.parentBloodType &&
                                    touched.parentBloodType
                                  }
                                >
                                  <Field
                                    as={Select}
                                    name="parentBloodType"
                                    displayEmpty
                                    inputProps={{
                                      "aria-label": "Parent Blood Type",
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>
                                        -- Select Blood Type (Optional) --
                                      </em>
                                    </MenuItem>
                                    {bloodTypeOptions.map((bloodType) => (
                                      <MenuItem
                                        key={bloodType.value}
                                        value={bloodType.value}
                                      >
                                        {bloodType.label}
                                      </MenuItem>
                                    ))}
                                  </Field>
                                  <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  display="block"
                                  mb={1}
                                >
                                  This field is optional. If you know your blood type, please select it.
                                </Typography>
                                  <FormHelperText>
                                    {errors.parentBloodType &&
                                    touched.parentBloodType
                                      ? errors.parentBloodType
                                      : ""}
                                  </FormHelperText>

                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Typography mt={1}>Height(cm):</Typography>
                                <Field
                                  as={TextField}
                                  name="parentHeight"
                                  size="small"
                                  type="number"
                                  fullWidth
                                  helperText={
                                    touched.parentHeight && errors.parentHeight
                                  }
                                  error={
                                    errors.parentHeight && touched.parentHeight
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Typography mt={1}>Weight(kg):</Typography>
                                <Field
                                  as={TextField}
                                  name="parentWeight"
                                  size="small"
                                  type="number"
                                  fullWidth
                                  helperText={
                                    touched.parentWeight && errors.parentWeight
                                  }
                                  error={
                                    errors.parentWeight && touched.parentWeight
                                  }
                                />
                              </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={3}>
                                <Typography mt={1}>
                                  Educational Attainment: <span style={{color: 'red'}}>*</span>
                                </Typography>
                                <FormControl fullWidth size="small">
                                  <Field
                                    as={Select}
                                    name="parentEducationalAttainment"
                                    helperText={
                                      touched.parentEducationalAttainment &&
                                      errors.parentEducationalAttainment
                                    }
                                    error={
                                      touched.parentEducationalAttainment &&
                                      errors.parentEducationalAttainment
                                    }
                                  >
                                    {educationalAttainmentOptions.map(
                                      (status) => (
                                        <MenuItem
                                          key={status.value}
                                          value={status.value}
                                        >
                                          {status.label}
                                        </MenuItem>
                                      )
                                    )}
                                  </Field>
                                  <FormHelperText>
                                    {touched.parentEducationalAttainment &&
                                      errors.parentEducationalAttainment}
                                  </FormHelperText>
                                </FormControl>
                                {values.parentEducationalAttainment ===
                                  "IbaPa" && (
                                  <Box mt={1}>
                                    <Field
                                      as={TextField}
                                      name="parentSpecificEducationalAttainment"
                                      label="Please specify"
                                      size="small"
                                      fullWidth
                                      helperText={
                                        touched.parentSpecificEducationalAttainment &&
                                        errors.parentSpecificEducationalAttainment
                                      }
                                      error={
                                        errors.parentSpecificEducationalAttainment &&
                                        touched.parentSpecificEducationalAttainment
                                      }
                                      onChange={(e) =>
                                        handleChange(e, setFieldValue)
                                      }
                                    />
                                  </Box>
                                )}
                              </Grid>
                              <Grid item xs={12} md={9}>
                                <Typography mt={1}>
                                  Contact Information:
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={3}>
                                    <Field
                                      as={TextField}
                                      name="parentEmailAddress"
                                      label={<>Email Address <span style={{color: 'red'}}>*</span></>}
                                      size="small"
                                      fullWidth
                                      disabled={parentQcidPopulatedFields.has("parentEmailAddress")}
                                      helperText={
                                        touched.parentEmailAddress &&
                                        errors.parentEmailAddress
                                      }
                                      error={
                                        errors.parentEmailAddress &&
                                        touched.parentEmailAddress
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <Field
                                      as={TextField}
                                      name="parentLandline"
                                      label="Landline"
                                      size="small"
                                      type="number"
                                      fullWidth
                                      helperText={
                                        touched.parentLandline &&
                                        errors.parentLandline
                                      }
                                      error={
                                        errors.parentLandline &&
                                        touched.parentLandline
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <Field
                                      as={TextField}
                                      name="parentCellphone"
                                      label={<>Cellphone <span style={{color: 'red'}}>*</span></>}
                                      size="small"
                                      type="text"
                                      fullWidth
                                      helperText={
                                        touched.parentCellphone &&
                                        errors.parentCellphone
                                      }
                                      error={
                                        errors.parentCellphone &&
                                        touched.parentCellphone
                                      }
                                      onChange={(e) =>
                                        handleChangeNumbers(e, setFieldValue)
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <Field
                                      as={TextField}
                                      id="parentFacebook"
                                      name="parentFacebook"
                                      label={<>Facebook name <span style={{color: 'red'}}>*</span></>}
                                      size="small"
                                      fullWidth
                                      inputProps={{ 'aria-label': 'Facebook name' }}
                                      helperText={
                                        touched.parentFacebook &&
                                        errors.parentFacebook
                                      }
                                      error={
                                        errors.parentFacebook &&
                                        touched.parentFacebook
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Field
                                      as={TextField}
                                      id="parentFacebookLink"
                                      name="parentFacebookLink"
                                      label={<>Facebook link <span style={{color: 'red'}}>*</span></>}
                                      size="small"
                                      fullWidth
                                      inputProps={{ 'aria-label': 'Facebook link' }}
                                      helperText={
                                        touched.parentFacebookLink &&
                                        errors.parentFacebookLink
                                      }
                                      error={
                                        errors.parentFacebookLink &&
                                        touched.parentFacebookLink
                                      }
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Typography mt={1}>
                              Miyembro ng PWD-related Organization? <span style={{color: 'red'}}>*</span>
                            </Typography>
                            <Field as={RadioGroup} name="parentPwdRelatedOrg">
                              <FormControlLabel
                                value="Oo"
                                control={<Radio size="small" />}
                                label="Oo"
                              />
                              <FormControlLabel
                                value="Hindi"
                                control={<Radio size="small" />}
                                label="Hindi"
                              />
                              <FormHelperText>
                                {touched.parentPwdRelatedOrg &&
                                  errors.parentPwdRelatedOrg}
                              </FormHelperText>
                            </Field>
                            {values.parentPwdRelatedOrg == "Oo" && (
                              <Box mb={1}>
                                <Field
                                  as={TextField}
                                  name="parentPwdRelatedOrgName"
                                  placeholder="Ano ito? *"
                                  size="small"
                                  fullWidth
                                  helperText={
                                    touched.parentPwdRelatedOrgName &&
                                    errors.parentPwdRelatedOrgName
                                  }
                                  error={
                                    touched.parentPwdRelatedOrgName &&
                                    errors.parentPwdRelatedOrgName
                                  }
                                  onChange={(e) =>
                                    handleChange(e, setFieldValue)
                                  }
                                />
                              </Box>
                            )}
                            <Typography mt={2}>
                              Sectoral Membership (I- check lahat ng kinabibilangan)
                            </Typography>
                            {parentSectoralMembershipOptions.map((option) => {
                              const selectedValues = Array.isArray(values.parentSectoralMembership)
                                ? values.parentSectoralMembership
                                : [];

                              return (
                                <React.Fragment key={option.value}>
                                  <FormControlLabel
                                    control={
                                      <Field
                                        type="checkbox"
                                        name="parentSectoralMembership"
                                        value={option.value}
                                        as={Checkbox}
                                        checked={selectedValues.includes(option.value)}
                                        onChange={() => {
                                          if (selectedValues.includes(option.value)) {
                                            const nextValue = selectedValues.filter(
                                              (value) => value !== option.value
                                            );
                                            setFieldValue(
                                              "parentSectoralMembership",
                                              nextValue
                                            );
                                          } else {
                                            const nextValue = [
                                              ...selectedValues,
                                              option.value,
                                            ];
                                            setFieldValue(
                                              "parentSectoralMembership",
                                              nextValue
                                            );
                                          }
                                        }}
                                      />
                                    }
                                    label={option.label}
                                  />
                                </React.Fragment>
                              );
                            })}
                            <FormHelperText>
                              {touched.parentSectoralMembership &&
                                errors.parentSectoralMembership}
                            </FormHelperText>
                            <Typography mt={2}>
                              Social Protection (I- check lahat ng kinabibilangan)
                            </Typography>
                            {parentSocialProtectionOptions.map((option) => {
                              const selectedValues = Array.isArray(values.parentSocialProtection)
                                ? values.parentSocialProtection
                                : [];

                              return (
                                <div key={option.value}>
                                  <FormControlLabel
                                    control={
                                      <Field
                                        type="checkbox"
                                        name="parentSocialProtection"
                                        value={option.value}
                                        as={Checkbox}
                                        checked={selectedValues.includes(option.value)}
                                        onChange={() => {
                                          if (selectedValues.includes(option.value)) {
                                            const nextValue = selectedValues.filter(
                                              (value) => value !== option.value
                                            );
                                            setFieldValue(
                                              "parentSocialProtection",
                                              nextValue
                                            );
                                          } else {
                                            const nextValue = [
                                              ...selectedValues,
                                              option.value,
                                            ];
                                            setFieldValue(
                                              "parentSocialProtection",
                                              nextValue
                                            );
                                          }
                                        }}
                                      />
                                    }
                                    label={option.label}
                                  />
                                </div>
                              );
                            })}
                            <FormHelperText>
                              {touched.parentSocialProtection &&
                                errors.parentSocialProtection}
                            </FormHelperText>
                            <Typography mt={2}>COVID 19 Vaccinated?</Typography>
                            <Field as={RadioGroup} name="parentVaccineStatus">
                              <FormControlLabel
                                value="Oo"
                                control={<Radio size="small" />}
                                label="Oo"
                              />
                              <FormControlLabel
                                value="Hindi"
                                control={<Radio size="small" />}
                                label="Hindi"
                              />
                              <FormHelperText>
                                {touched.parentVaccineStatus &&
                                  errors.parentVaccineStatus}
                              </FormHelperText>
                            </Field>
                            {values.parentVaccineStatus == "Oo" && (
                              <Box>
                                <Typography>Pang ilan?</Typography>
                                <FormControl fullWidth size="small">
                                  <Field
                                    as={Select}
                                    name="parentVaccineShotCount"
                                    helperText={
                                      touched.parentVaccineShotCount &&
                                      errors.parentVaccineShotCount
                                    }
                                    error={
                                      touched.parentVaccineShotCount &&
                                      errors.parentVaccineShotCount
                                    }
                                  >
                                    <MenuItem value={"1st Dose"}>
                                      1st Dose
                                    </MenuItem>
                                    <MenuItem value={"2nd Dose"}>
                                      2nd Dose
                                    </MenuItem>
                                    <MenuItem value={"2nd Dose + Booster"}>
                                      2nd Dose + Booster
                                    </MenuItem>
                                  </Field>
                                  <FormHelperText>
                                    {touched.parentVaccineShotCount &&
                                      errors.parentVaccineShotCount}
                                  </FormHelperText>
                                </FormControl>
                              </Box>
                            )}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Badge>
                </div>
              </Box>
              <Box>
                <Box mt={2}></Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    pt: 2,
                    gap: 2,
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  {!isValid && (
                    <Box mr={2}>
                      <Typography variant="body2" color="error">
                        Please complete all required fields
                      </Typography>
                    </Box>
                  )}
                  <Button
                    type="submit"
                    aria-label="Next"
                    disabled={isSubmitting}
                    variant="contained"
                    color="primary"
                    sx={{
                      width: { xs: "auto", sm: "auto" },
                    }}
                    onClick={(e) => {
                      if (!isValid) {
                      }
                    }}
                  >
                    {isSubmitting ? "Processing..." : "Next"}
                  </Button>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
      
      {/* QC ID Loading UI */}
      {(isQcidLoading || isParentQcidLoading) && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            animation: 'fadeIn 0.3s ease-in-out'
          }}
        >
          <div 
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              padding: '40px 60px',
              textAlign: 'center',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25), 0 10px 40px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              maxWidth: '400px',
              margin: '20px',
              animation: 'slideInUp 0.4s ease-out'
            }}
          >
            {/* Professional Spinner */}
            <div
              style={{
                width: '60px',
                height: '60px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #26516F',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 24px auto'
              }}
            />
            
            {/* Loading Text */}
            <p 
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#26516F',
                letterSpacing: '0.5px',
                lineHeight: '1.4'
              }}
            >
              {isQcidLoading ? 'Retrieving Child QC ID Information...' : 'Retrieving Parent QC ID Information...'}
            </p>
            
            {/* Subtitle */}
            <p 
              style={{
                margin: '8px 0 0 0',
                fontSize: '14px',
                color: '#666',
                fontWeight: '400'
              }}
            >
              Please wait a moment...
            </p>
            
            {/* Add keyframe animations via style tag */}
            <style jsx="true">{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              
              @keyframes slideInUp {
                from { 
                  opacity: 0;
                  transform: translateY(30px) scale(0.95);
                }
                to { 
                  opacity: 1;
                  transform: translateY(0) scale(1);
                }
              }
            `}</style>
          </div>
        </div>
      )}
    </Box>
  );
}
export default PersonalInformation;
